import type { ID } from '@octopus-do/octopus-project-core'

import { noop } from '@/helpers'

// What the app reports about itself when a save goes wrong on the client
// (OCT-5217). Lands in Cloud Logging of octopus-api as `client_event`.
export type ClientEventType =
  | 'editor_text_lost'
  | 'commit_dropped'
  | 'commit_failed'
  | 'socket_disconnect'
  | 'socket_reconnect_failed'

export interface ClientEvent {
  type: ClientEventType
  // A short discriminator: the extension found, the HTTP status, the
  // socket.io disconnect reason. Never user content.
  reason?: string
  // What the page looked like at the moment: foreign elements, extension
  // markers, input event counts. Names and numbers only, never content.
  details?: string
  // A number that goes with the type, such as the count of lost characters.
  value?: number
  projectId?: ID | null
}

const readToken = (): string | null => {
  try {
    return localStorage.getItem('token')
  } catch {
    return null
  }
}

// Nest reads the same `X-Auth-Token` as the rest of the platform; a visitor
// reports without it. `keepalive` lets the report leave with a closing tab.
// A report is a side note to the save path it describes: nothing here may
// throw into that path, so every failure is swallowed.
export const reportClientEvent = (event: ClientEvent): void => {
  try {
    const token = readToken()

    fetch(`${import.meta.env.VITE_NEST_URL}/client-events`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'X-Auth-Token': token } : null),
      },
      body: JSON.stringify(event),
      keepalive: true,
    }).catch(noop)
  } catch {
    noop()
  }
}
